import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';
import { useLocation } from 'react-router-dom';
import { getLocalizedPath, getRouteKeyFromPath, supportedLanguages, defaultLanguage } from '@/config/routes';

const siteUrl = 'https://netblitz.eu';

export const SEO = ({ 
  title, 
  description, 
  keywords,
  image = '/og-image.jpg',
  type = 'website',
  noIndex = false 
}) => {
  const { language } = useLanguage();
  const location = useLocation();
  
  const fullTitle = title ? `${title} | Netblitz` : 'Netblitz - Professionele Websites voor Ondernemers';
  const canonicalUrl = `${siteUrl}${location.pathname}`;

  // Build hreflang alternates from the localized route config
  const segments = location.pathname.split('/').filter(Boolean);
  const routeKey = getRouteKeyFromPath(segments[1]);
  const tail = segments.slice(2).join('/');
  const alternates = routeKey
    ? supportedLanguages.map((lng) => {
        const base = getLocalizedPath(routeKey, lng);
        return { lang: lng, href: `${siteUrl}${tail ? `${base}/${tail}` : base}` };
      })
    : [];
  const xDefaultHref = routeKey
    ? `${siteUrl}${getLocalizedPath(routeKey, defaultLanguage)}${tail ? `/${tail}` : ''}`
    : null;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="Netblitz" />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${siteUrl}${image}`} />
      <meta property="og:image:width" content="1536" />
      <meta property="og:image:height" content="1024" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Netblitz" />
      <meta property="og:locale" content={language === 'nl' ? 'nl_NL' : language === 'en' ? 'en_US' : language === 'fr' ? 'fr_FR' : 'de_DE'} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${siteUrl}${image}`} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang alternates */}
      {alternates.map((alt) => (
        <link key={alt.lang} rel="alternate" hrefLang={alt.lang} href={alt.href} />
      ))}
      {xDefaultHref && <link rel="alternate" hrefLang="x-default" href={xDefaultHref} />}
      
      {/* Additional Meta */}
      <meta name="theme-color" content="#2563EB" />
      <meta name="format-detection" content="telephone=no" />
    </Helmet>
  );
};

// Structured Data for Organization
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Netblitz",
    "url": "https://netblitz.eu",
    "logo": "https://netblitz.eu/favicon.svg",
    "description": "Professionele websites voor zelfstandigen en KMO's in België",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Kunstlaan 50",
      "postalCode": "1000",
      "addressLocality": "Brussels",
      "addressRegion": "Brussels Capital Region",
      "addressCountry": "BE"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+32-2-123-4567",
      "email": "hello@netblitz.eu",
      "contactType": "customer service",
      "availableLanguage": ["Dutch", "English", "French", "German"]
    },
    "sameAs": [
      "https://www.linkedin.com/company/netblitz",
      "https://www.facebook.com/netblitz",
      "https://www.instagram.com/netblitz"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for Local Business
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://netblitz.eu/#localbusiness",
    "name": "Netblitz",
    "image": "https://netblitz.eu/og-image.jpg",
    "url": "https://netblitz.eu",
    "telephone": "+32-2-123-4567",
    "email": "hello@netblitz.eu",
    "description": "Netblitz bouwt professionele websites voor ondernemers in België. Snel, betaalbaar en SEO-geoptimaliseerd. Vanaf €599.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Kunstlaan 50",
      "postalCode": "1000",
      "addressLocality": "Brussels",
      "addressRegion": "Brussels Capital Region",
      "addressCountry": "BE"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 50.8467,
      "longitude": 4.3525
    },
    "priceRange": "€€",
    "currenciesAccepted": "EUR",
    "paymentAccepted": "Cash, Credit Card, Bank Transfer",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    ],
    "areaServed": [
      {
        "@type": "Country",
        "name": "Belgium"
      },
      {
        "@type": "Country",
        "name": "Netherlands"
      },
      {
        "@type": "Country", 
        "name": "Luxembourg"
      }
    ],
    "serviceType": ["Web Design", "Web Development", "SEO Optimization", "E-commerce Development"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Website Packages",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Starter Website"
          },
          "price": "599",
          "priceCurrency": "EUR"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Professional Website"
          },
          "price": "899",
          "priceCurrency": "EUR"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Premium Website"
          },
          "price": "1499",
          "priceCurrency": "EUR"
        }
      ]
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for Services
export const ServiceSchema = ({ name, description, price }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": name,
    "provider": {
      "@type": "Organization",
      "name": "Netblitz"
    },
    "description": description,
    "areaServed": "Worldwide",
    ...(price && {
      "offers": {
        "@type": "Offer",
        "price": price,
        "priceCurrency": "EUR"
      }
    })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for FAQ
export const FAQSchema = ({ faqs }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for Blog Post
export const BlogPostSchema = ({ title, description, image, datePublished, author }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "image": image,
    "datePublished": datePublished,
    "author": {
      "@type": "Person",
      "name": author || "Netblitz"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Netblitz",
      "logo": {
        "@type": "ImageObject",
        "url": "https://netblitz.eu/favicon.svg"
      }
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for Breadcrumbs
export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://netblitz.eu${item.url}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for Website with Search
export const WebsiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Netblitz",
    "url": "https://netblitz.eu",
    "description": "Professionele websites voor ondernemers en bedrijven in België en Nederland",
    "inLanguage": ["nl", "en", "fr", "de"],
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://netblitz.eu/blog?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for Product/Pricing
export const ProductSchema = ({ name, description, price }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": name,
    "description": description,
    "brand": {
      "@type": "Brand",
      "name": "Netblitz"
    },
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};
